import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Modal, Button } from "@mui/material";
import { useAppDispatch } from "../../store/hooks";
import * as Styled from "./AddAttendeeModal.styles";

import ChipListInputDropDown from "../../components/ChipListInputDropdown/ChipListInputDropDown";
import { RootState } from "../../store/store";
import { CloseIcon } from "../../components/Chip/Chip.styles";
import axiosAPI from "../../services/axios";
import { GET_USERS_EMAILS, JOIN_EVENT_MANUALLY } from "../../services/endpoints";
import { refreshLiveEventAttendees } from "../../store/actions/liveEventActions";
import { useSelector } from "react-redux";

type User = {
  email: string;
  name: string;
};

type AddAttendeeModalProps = {
  open: boolean;
  onClose: () => void;
};

const AddAttendeeModal = ({ open, onClose }: AddAttendeeModalProps) => {
  const { control, handleSubmit, reset } = useForm();

  const [error, setError] = useState("");

  const isUserAdmin = useSelector((state: RootState) => {
    const role = state.user?.role;
    return role === "ADMIN" || role === "ADMIN:SUPER";
  });

  const liveEvent = useSelector((state: RootState) => state.liveEvent.data);
  const participants = useSelector((state: RootState) => state.liveEvent.attendees?.data || []);
  const [users, setUsers] = useState([])
  const dispatch = useAppDispatch();


   useEffect(() => {
    const fetchUsers = async () => {
      try {
        const { data } = await axiosAPI.request({
          url: GET_USERS_EMAILS(),
          method: "GET",
        });
        
        const transformedUsers = data.result.users.map((user: { email: string; firstName: string; lastName: string; }) => ({
          email: user.email,
          name: `${user.firstName} ${user.lastName}`.trim()
        }));
        
        setUsers(transformedUsers);
      } catch (error) {
        console.error("Error fetching user emails:", error);
      }
    };
  
    fetchUsers();
    
  }, []);

  useEffect(() => {
    if (!open) {
      reset();
      setError("");
    }
  }, [open, reset]);

  const handleSave = async (formData: any) => {
    const selected: User[] = formData.attendees;
    if (selected.length === 0 ){
      setError('Please enter a user to proceed')
    }
    if (!liveEvent || selected.length === 0) return;

    const selectedParticipants = participants.filter((p) =>
      selected.some((u) => u.email === p.participantEmail)
    );

    const alreadyCheckedIn = selectedParticipants.filter(
      (p) => p.checkedIn === true
    );

    if (alreadyCheckedIn.length > 0) {
      for (let user in alreadyCheckedIn){
      setError(alreadyCheckedIn[user].participantName+ " is already checked-in to this event. Please remove them in order to proceed.");
      }
      return;
    }
    // console.log("formData ", formData)
    try {
      await Promise.all(
        selected.map((p) =>
          axiosAPI.post(JOIN_EVENT_MANUALLY(liveEvent.eventId), {
            participantEmail: p.email,
            participantName: p.name,
            participantTitle: 'NA',
            manuallyAdded: true,
          })
        )
      );
      dispatch(refreshLiveEventAttendees(liveEvent.eventId));
      onClose();
    } catch (error) {
      setError("Something went wrong. Please try again.");
    }
  };

  if (!isUserAdmin) return null;

  return (
    <Modal open={open} onClose={onClose}>
      <Styled.ModalContainer>
        <Styled.ModalHeader>
          <Styled.ModalTitle>Add attendee</Styled.ModalTitle>
          <Styled.CloseButton onClick={onClose}>
            <CloseIcon />
          </Styled.CloseButton>
        </Styled.ModalHeader>

        <Styled.ModalDescription>
          Use the field below to search and select name(s) of colleagues to add as an attendee for this event.
        </Styled.ModalDescription>

        <ChipListInputDropDown
          name="attendees"
          control={control}
          label="Name(s)*"
          placeholder="Search or select..."
          options={users}
          fullWidth
          required
        />

        {error && <Styled.ErrorMessage>{error}</Styled.ErrorMessage>}

        <Styled.ActionsContainer>
          <Button onClick={handleSubmit(handleSave)} variant="outlined">
            Save
          </Button>
        </Styled.ActionsContainer>
      </Styled.ModalContainer>
    </Modal>
  );
};

export default AddAttendeeModal;
